import { lazy, Suspense, ElementType } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';

import { PATH_ADMIN, PATH_DOCUMENTS } from 'src/routes/paths';

import RoleGuard from 'src/guards/RoleGuard';
import AuthLayout from 'src/layouts/AuthLayout';
import ModuleGuard from 'src/guards/ModuleGuard';
import Folders from 'src/pages/documents/Folders';
import List from 'src/pages/admin/customLists/List';
import Dashboard from 'src/pages/dashboard/Dashboard';
import { Roles } from 'src/@types/auth/constants/roles';
import PermissionGuard from 'src/guards/PermissionGuard';
import { ModuleTypes } from 'src/@types/module/enums/moduleTypes';
import { DocumentsTypeProvider } from 'src/contexts/DocumentsTypeContext';
import { DocumentModuleTypes } from 'src/@types/shared/enums/documentModuleTypes';
import { Permissions as PermissionTypes } from 'src/@types/auth/constants/permissions';

import Departments from 'src/sections/admin/departments/Departments';

import Form from '../sections/forms/Form';
import AuthGuard from '../guards/AuthGuard';
import GuestGuard from '../guards/GuestGuard';
import Forms from '../pages/admin/forms/Forms';
import Calendar from '../pages/calendar/Calendar';
import DashboardLayout from '../layouts/dashboard';
import DLXDrive from '../pages/dlx-drive/DLXDrive';
import EditForm from '../pages/admin/forms/EditForm';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
import LoadingScreen from '../components/LoadingScreen';
import CreateForm from '../pages/admin/forms/CreatForm';
import EventTypes from '../pages/admin/calendar/EventTypes';
import AccessGroups from '../pages/admin/dlxDrive/AccessGroups';
import EparakstsCardCallback from '../sections/documents/EparakstsCardCallback';
import EparakstsMobileCallback from '../sections/documents/EparakstsMobileCallback';

// ----------------------------------------------------------------------

const Loadable = (Component: ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/documents')} />}>
      <Component {...props} />
    </Suspense>
  );
};

const DefaultPage = () => <Navigate to={PATH_DOCUMENTS.root} replace />;

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      element: (
        <GuestGuard>
          <AuthLayout />
        </GuestGuard>
      ),
      children: [
        {
          path: 'login',
          element: <Login />,
        },
        {
          path: 'register',
          element: (
            <ModuleGuard moduleKey={ModuleTypes.EmailLogin}>
              <Register />
            </ModuleGuard>
          ),
        },
        {
          path: 'reset-password',
          element: (
            <ModuleGuard moduleKey={ModuleTypes.EmailLogin}>
              <ResetPassword />
            </ModuleGuard>
          ),
        },
        {
          path: 'password-recovery',
          element: (
            <ModuleGuard moduleKey={ModuleTypes.EmailLogin}>
              <PasswordRecovery />
            </ModuleGuard>
          ),
        },
        {
          path: 'confirm-email',
          element: <ConfirmEmail />,
        },
        {
          path: 'eparaksts',
          element: (
            <ModuleGuard moduleKey={ModuleTypes.EparakstLogin}>
              <EparakstsRedirect />
            </ModuleGuard>
          ),
        },
      ],
    },

    // Dashboard
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <ModuleGuard moduleKey={ModuleTypes.Dashboard}>
            <DashboardLayout />
          </ModuleGuard>
        </AuthGuard>
      ),
      children: [{ element: <Dashboard />, index: true }],
    },

    // Global Search
    {
      path: 'search',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [{ element: <GlobalSearchPage />, index: true }],
    },

    // HR Routes
    {
      path: 'module-b',
      element: (
        <AuthGuard>
          <ModuleGuard moduleKey={ModuleTypes.HR}>
            <PermissionGuard permission={PermissionTypes.UseHRModule}>
              <DocumentsTypeProvider documentType={DocumentModuleTypes.HR}>
                <DashboardLayout />
              </DocumentsTypeProvider>
            </PermissionGuard>
          </ModuleGuard>
        </AuthGuard>
      ),
      children: [
        {
          path: 'create-document',
          children: [
            {
              element: (
                <PermissionGuard permission={PermissionTypes.ComposeDocument}>
                  <HRFormFill />
                </PermissionGuard>
              ),
              index: true,
            },
            {
              path: ':formBuilderId',
              children: [
                {
                  element: (
                    <PermissionGuard permission={PermissionTypes.ComposeDocument}>
                      <HRFormFill />
                    </PermissionGuard>
                  ),
                  index: true,
                },
                {
                  path: ':folderId',
                  children: [
                    {
                      element: (
                        <PermissionGuard permission={PermissionTypes.ComposeDocument}>
                          <HRFormFill />
                        </PermissionGuard>
                      ),
                      index: true,
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },

    // Accounting Routes
    {
      path: 'module-c',
      element: (
        <AuthGuard>
          <ModuleGuard moduleKey={ModuleTypes.Accounting}>
            <PermissionGuard permission={PermissionTypes.UseAccountingModule}>
              <DocumentsTypeProvider documentType={DocumentModuleTypes.Accounting}>
                <DashboardLayout />
              </DocumentsTypeProvider>
            </PermissionGuard>
          </ModuleGuard>
        </AuthGuard>
      ),
      children: [
        {
          path: 'documents',
          children: [
            {
              path: ':id',
              children: [
                { element: <DocumentDetails />, index: true },
                {
                  path: 'eparaksts-mobile/callback',
                  element: <EparakstsMobileCallback />,
                },
              ],
            },
          ],
        },
        {
          path: '/module-c/documents/eparaksts-mobile/callback',
          element: <EparakstsCardCallback />,
        },
        {
          path: 'create-document',
          children: [
            {
              element: (
                <PermissionGuard permission={PermissionTypes.ComposeDocument}>
                  <AccountancyFormFill />
                </PermissionGuard>
              ),
              index: true,
            },
            {
              path: ':formBuilderId',
              children: [
                {
                  element: (
                    <PermissionGuard permission={PermissionTypes.ComposeDocument}>
                      <AccountancyFormFill />
                    </PermissionGuard>
                  ),
                  index: true,
                },
                {
                  path: ':folderId',
                  children: [
                    {
                      element: (
                        <PermissionGuard permission={PermissionTypes.ComposeDocument}>
                          <AccountancyFormFill />
                        </PermissionGuard>
                      ),
                      index: true,
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },

    //  DLX Drive
    {
      path: 'folders',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: '/folders',
          element: <DLXDrive />,
        },
        {
          path: '/folders/:id',
          element: <DLXDrive />,
        },
        {
          path: '/folders/:id/:page',
          element: <DLXDrive />,
        },
        {
          path: '/folders/:id/:page/eparaksts-mobile/callback',
          element: <EparakstsCardCallback />,
        },
      ],
    },

    // Calendar
    {
      path: 'calendar',
      element: (
        <AuthGuard>
          <ModuleGuard moduleKey={ModuleTypes.Calendar}>
            <DashboardLayout />
          </ModuleGuard>
        </AuthGuard>
      ),
      children: [
        {
          path: '/calendar',
          element: <Calendar />,
        },
      ],
    },

    //  DLX Drive
    {
      path: 'documents',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: '/documents',
          element: <Folders />,
        },
        {
          path: '/documents/:folderId',
          element: <Folders />,
        },
        {
          path: '/documents/:folderId/:formId/:formType/create-document',
          element: <Form />,
        },
        {
          path: '/documents/:folderId/:formId/:formType/:documentId/edit-document',
          element: <Form />,
        },
        {
          path: '/documents/eparaksts-mobile/callback',
          element: <EparakstsCardCallback />,
        },
        {
          path: '/documents/:folderId/eparaksts-mobile/callback',
          element: <EparakstsCardCallback />,
        },
      ],
    },

    // Standard Routes
    {
      path: 'standard',
      element: (
        <AuthGuard>
          <ModuleGuard moduleKey={ModuleTypes.Standard}>
            <DocumentsTypeProvider documentType={DocumentModuleTypes.Standard}>
              <DashboardLayout />
            </DocumentsTypeProvider>
          </ModuleGuard>
        </AuthGuard>
      ),
      children: [
        {
          path: 'documents',
          children: [
            {
              path: ':id',
              children: [
                { element: <DocumentDetails />, index: true },
                {
                  path: 'eparaksts-mobile/callback',
                  element: <EparakstsMobileCallback />,
                },
              ],
            },
          ],
        },
        {
          path: 'compose-document',
          children: [
            {
              element: (
                <PermissionGuard permission={PermissionTypes.ComposeDocument}>
                  <DocumentUpload />
                </PermissionGuard>
              ),
              index: true,
            },
            {
              path: ':id',
              children: [
                {
                  element: (
                    <PermissionGuard permission={PermissionTypes.ComposeDocument}>
                      <DocumentUpload />
                    </PermissionGuard>
                  ),
                  index: true,
                },
              ],
            },
          ],
        },
      ],
    },

    // Profile Routes
    {
      path: 'profile',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [{ element: <Profile />, index: true }],
    },

    // Address book
    {
      path: 'addressBook',
      element: (
        <AuthGuard>
          <ModuleGuard moduleKey={ModuleTypes.AddressBook}>
            <PermissionGuard permission={PermissionTypes.UseAddressBook}>
              <DashboardLayout />
            </PermissionGuard>
          </ModuleGuard>
        </AuthGuard>
      ),
      children: [{ element: <AddressBook />, index: true }],
    },

    // Admin Routes
    {
      path: 'admin',
      element: (
        <AuthGuard>
          <RoleGuard accessibleRoles={[Roles.Admin, Roles.SuperAdmin]}>
            <DashboardLayout />
          </RoleGuard>
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_ADMIN.adminPanel} replace />, index: true },
        {
          path: 'admin-panel',
          element: <AdminPanel />,
        },
        {
          path: 'users',
          element: <Users />,
          children: [
            {
              path: ':searchQuery',
              element: <Users />,
            },
          ],
        },
        {
          path: 'permissions',
          element: <Permissions />,
        },
        {
          path: 'company',
          element: <Company />,
        },
        {
          path: 'email-templates-settings',
          element: <EmailTemplates />,
        },
        {
          path: 'billing-report',
          element: <BillingReport />,
        },
        {
          path: 'access-groups',
          element: <AccessGroups />,
        },
        {
          path: 'event-types',
          element: <EventTypes />,
        },
        {
          path: 'departments',
          element: <Departments />,
        },

        // Form Routes (Admin part)
        {
          path: 'forms',
          children: [{ element: <Forms />, index: true }],
        },
        {
          path: 'forms/create',
          children: [{ element: <CreateForm />, index: true }],
        },
        {
          path: 'forms/edit',
          children: [
            {
              path: ':id',
              element: <EditForm />,
            },
          ],
        },
        {
          path: 'lists',
          children: [{ element: <List />, index: true }],
        },
        {
          path: 'tags',
          element: (
            <ModuleGuard moduleKey={ModuleTypes.Tags}>
              <Tags />
            </ModuleGuard>
          ),
        },
        {
          path: 'job-titles',
          element: (
            <ModuleGuard moduleKey={ModuleTypes.CustomJobTitles}>
              <JobTitles />
            </ModuleGuard>
          ),
        },
        {
          path: 'workflows',
          children: [
            {
              element: (
                <ModuleGuard moduleKey={ModuleTypes.WorkflowsModule}>
                  <Workflows />
                </ModuleGuard>
              ),
              index: true,
            },
          ],
        },
        {
          path: 'workflows/create',
          children: [
            {
              element: (
                <ModuleGuard moduleKey={ModuleTypes.WorkflowsModule}>
                  <WorkflowDefinitionCreate />
                </ModuleGuard>
              ),
              index: true,
            },
          ],
        },
        {
          path: 'workflows/:definitionNumber/edit',
          children: [
            {
              element: (
                <ModuleGuard moduleKey={ModuleTypes.WorkflowsModule}>
                  <WorkflowDefinitionEdit />
                </ModuleGuard>
              ),
              index: true,
            },
          ],
        },
      ],
    },

    // Super Admin Routes
    {
      path: 'super-admin',
      element: (
        <AuthGuard>
          <RoleGuard accessibleRoles={[Roles.SuperAdmin]}>
            <DashboardLayout />
          </RoleGuard>
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/super-admin/tenants" replace />, index: true },
        {
          path: 'tenants',
          children: [
            { element: <Tenants />, index: true },
            {
              path: 'create',
              element: <TenantCreate />,
            },
            {
              path: ':id',
              children: [
                {
                  path: 'edit',
                  index: true,
                  element: <TenantEdit />,
                },
              ],
            },
          ],
        },
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      children: [{ element: <DefaultPage />, index: true }],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// IMPORT COMPONENTS

// Authentication
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const PasswordRecovery = Loadable(lazy(() => import('../pages/auth/PasswordRecovery')));
const ConfirmEmail = Loadable(lazy(() => import('../pages/auth/ConfirmEmail')));
const EparakstsRedirect = Loadable(lazy(() => import('../pages/auth/EparakstsRedirect')));
// Documents
const DocumentUpload = Loadable(lazy(() => import('../pages/documents/DocumentUpload')));
const DocumentDetails = Loadable(lazy(() => import('../pages/documents/DocumentDetails')));
// Main
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
// Admin
const AdminPanel = Loadable(lazy(() => import('../sections/admin/AdminPanel')));
const Users = Loadable(lazy(() => import('../pages/admin/Users')));
const Permissions = Loadable(lazy(() => import('../pages/admin/Permissions')));
const Company = Loadable(lazy(() => import('../pages/admin/Company')));
const EmailTemplates = Loadable(lazy(() => import('../pages/admin/EmailTemplates')));
const BillingReport = Loadable(lazy(() => import('../pages/admin/BillingReportPage')));

const Tags = Loadable(lazy(() => import('../pages/admin/tags/Tags')));
const JobTitles = Loadable(lazy(() => import('../pages/admin/jobTitles/JobTitles')));
const Workflows = Loadable(lazy(() => import('../pages/admin/workflow/Workflows')));
const WorkflowDefinitionCreate = Loadable(
  lazy(() => import('../pages/admin/workflow/WorkflowDefinitionCreate'))
);
const WorkflowDefinitionEdit = Loadable(
  lazy(() => import('../pages/admin/workflow/WorkflowDefinitionEdit'))
);
// Profile
const Profile = Loadable(lazy(() => import('../pages/profile/Profile')));
// Tenants
const Tenants = Loadable(lazy(() => import('../pages/super-admin/tenants/Tenants')));
const TenantCreate = Loadable(lazy(() => import('../pages/super-admin/tenants/TenantCreate')));
const TenantEdit = Loadable(lazy(() => import('../pages/super-admin/tenants/TenantEdit')));
// Form
const HRFormFill = Loadable(lazy(() => import('../pages/forms/HRFormFill')));
const AccountancyFormFill = Loadable(lazy(() => import('../pages/forms/AccountancyFormFill')));
// Address book
const AddressBook = Loadable(lazy(() => import('../pages/addressBook/AddressBook')));
// Search
const GlobalSearchPage = Loadable(lazy(() => import('../pages/search/GlobalSearchPage')));
